import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  Pencil,
  Check,
  Layers,
  Loader2,
  Tag,
} from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import DeletePopup from "../../../atoms/Dashboard/Folder/DeletePopup";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
  gradient,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  Input,
  FormLabelIcon,
  FormGradientDefs,
} from "../../../molecules/Dashboard/Member/memberFormStyles";
import {
  Category,
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../../../../Redux/ApiCalls/Dashboard/EquipmentAPI";
import { usePermissions } from "../../../../hooks/usePermissions";

interface CategoryManagerModalProps {
  onClose: () => void;
  onRefresh: () => void;
}

interface ConfirmState {
  type: "toggle" | "delete";
  cat: Category;
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const InputRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: stretch;
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 90px;
  height: 48px;
  padding: 0 16px;
  background: ${gradient};
  color: white;
  border: none;
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.22);
  transition: all 0.15s ease;
  &:hover:not(:disabled) { transform: translateY(-1px); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(23, 23, 23, 0.5);
  margin-bottom: 10px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  margin: 18px 0;
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 2px;
`;

const CategoryItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 14px;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.18);
    background: rgba(201, 77, 170, 0.03);
  }
`;

const CatLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const CatDot = styled.div<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.$active ? "#31a66a" : "rgba(23,23,23,0.2)")};
  flex-shrink: 0;
`;

const CatName = styled.div`
  font-family: Urbanist, sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #171717;
`;

const EditInput = styled(Input)`
  max-width: 180px;
  height: 38px;
  font-size: 14px;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const StatusToggle = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid ${(p) => (p.$active ? "rgba(49, 166, 106, 0.22)" : "rgba(23, 23, 23, 0.1)")};
  padding: 6px 10px;
  border-radius: 999px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${(p) => (p.$active ? "rgba(49, 166, 106, 0.1)" : "rgba(23, 23, 23, 0.04)")};
  color: ${(p) => (p.$active ? "#31a66a" : "rgba(23, 23, 23, 0.5)")};
  &:hover {
    border-color: ${(p) => (p.$active ? "rgba(228, 72, 98, 0.25)" : "rgba(49, 166, 106, 0.25)")};
    color: ${(p) => (p.$active ? "#e44862" : "#31a66a")};
  }
`;

const IconActionBtn = styled.button<{ $variant?: "edit" | "delete" | "save" | "cancel" }>`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${(p) =>
    p.$variant === "delete"
      ? "#e44862"
      : p.$variant === "save"
      ? "#31a66a"
      : "rgba(23, 23, 23, 0.55)"};
  &:hover {
    border-color: ${(p) =>
      p.$variant === "delete"
        ? "rgba(228, 72, 98, 0.25)"
        : p.$variant === "save"
        ? "rgba(49, 166, 106, 0.25)"
        : "rgba(201, 77, 170, 0.25)"};
    color: ${(p) =>
      p.$variant === "delete" ? "#e44862" : p.$variant === "save" ? "#31a66a" : "#c94daa"};
  }
`;

const SaveToggleBtn = styled(StatusToggle)`
  background: rgba(49, 166, 106, 0.1);
  color: #31a66a;
  border-color: rgba(49, 166, 106, 0.22);
`;

const EmptyText = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.45);
  text-align: center;
  padding: 28px 16px;
  border-radius: 14px;
  border: 1px dashed rgba(23, 23, 23, 0.1);
  background: #fafafa;
`;

const ConfirmFooter = styled.div`
  display: flex;
  gap: 10px;
  padding: 0 24px 24px;
`;

const SecondaryBtn = styled.button`
  flex: 1;
  height: 46px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 12px;
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: rgba(201, 77, 170, 0.25); color: #c94daa; }
`;

const ConfirmText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.68);
  line-height: 1.65;
`;

const CategoryManagerModal: React.FC<CategoryManagerModalProps> = ({ onClose, onRefresh }) => {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("equipment.create");
  const canUpdate = hasPermission("equipment.update");
  const canDelete = hasPermission("equipment.delete");
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCatName, setNewCatName] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");

  const fetchCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.results || []);
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to load categories");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleRenameCategory = async (id: number) => {
    if (!editingName.trim()) return;
    try {
      await updateCategory(id, { name: editingName.trim() });
      showSuccessToast("Category renamed successfully");
      setEditingId(null);
      fetchCategories();
      onRefresh();
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to rename category");
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCatName.trim()) return;
    setLoading(true);
    try {
      await createCategory({ name: newCatName.trim() });
      showSuccessToast("Category added successfully");
      setNewCatName("");
      fetchCategories();
      onRefresh();
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to add category");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirm) return;
    if (confirm.type === "toggle") {
      try {
        await updateCategory(confirm.cat.id, { is_active: !confirm.cat.is_active });
        showSuccessToast(
          `Category ${!confirm.cat.is_active ? "activated" : "deactivated"} successfully`
        );
        fetchCategories();
        onRefresh();
      } catch (err: any) {
        showErrorToast(err?.message || "Failed to update category status");
      }
    } else if (confirm.type === "delete") {
      try {
        await deleteCategory(confirm.cat.id);
        showSuccessToast("Category deleted successfully");
        fetchCategories();
        onRefresh();
      } catch (err: any) {
        showErrorToast(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to delete category (it may be in use by some equipment)"
        );
      }
    }
    setConfirm(null);
  };

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay onClick={onClose}>
        <ModalShell style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  <Layers size={11} />
                  Equipment
                </HeroBadge>
                <ModalTitle>Manage Categories</ModalTitle>
                <ModalSubtitle>
                  Add, rename, activate, or remove equipment categories.
                </ModalSubtitle>
              </HeroText>
              <CloseBtn type="button" onClick={onClose} aria-label="Close">
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            {canCreate && (
              <>
                <form onSubmit={handleAddCategory}>
                  <SectionLabel>
                    <FormLabelIcon icon={Plus} />
                    Add new category
                  </SectionLabel>
                  <InputRow>
                    <Input
                      type="text"
                      placeholder="e.g. Lens, Camera, Drone..."
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      required
                    />
                    <AddBtn type="submit" disabled={loading}>
                      {loading ? <Spinner size={16} /> : <Plus size={16} />}
                      Add
                    </AddBtn>
                  </InputRow>
                </form>

                <Divider />
              </>
            )}

            <SectionLabel>
              <FormLabelIcon icon={Tag} />
              Existing categories ({categories.length})
            </SectionLabel>
            <CategoryList>
              {categories.length === 0 ? (
                <EmptyText>No categories registered yet.</EmptyText>
              ) : (
                categories.map((cat) => (
                  <CategoryItem key={cat.id}>
                    <CatLeft>
                      <CatDot $active={cat.is_active} />
                      {editingId === cat.id ? (
                        <EditInput
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRenameCategory(cat.id);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                      ) : (
                        <CatName>{cat.name}</CatName>
                      )}
                    </CatLeft>
                    <ActionRow>
                      {editingId === cat.id ? (
                        <>
                          <SaveToggleBtn
                            type="button"
                            $active
                            onClick={() => handleRenameCategory(cat.id)}
                            title="Save name"
                          >
                            <Check size={14} /> Save
                          </SaveToggleBtn>
                          <IconActionBtn
                            type="button"
                            $variant="cancel"
                            onClick={() => setEditingId(null)}
                            title="Cancel"
                          >
                            <X size={14} />
                          </IconActionBtn>
                        </>
                      ) : (
                        <>
                          {canUpdate && (
                            <StatusToggle
                              type="button"
                              $active={cat.is_active}
                              onClick={() => setConfirm({ type: "toggle", cat })}
                              title={cat.is_active ? "Click to deactivate" : "Click to activate"}
                            >
                              {cat.is_active ? (
                                <>
                                  <ToggleRight size={16} /> Active
                                </>
                              ) : (
                                <>
                                  <ToggleLeft size={16} /> Inactive
                                </>
                              )}
                            </StatusToggle>
                          )}
                          {canUpdate && (
                            <IconActionBtn
                              type="button"
                              $variant="edit"
                              onClick={() => {
                                setEditingId(cat.id);
                                setEditingName(cat.name);
                              }}
                              title="Rename Category"
                            >
                              <Pencil size={14} />
                            </IconActionBtn>
                          )}
                          {canDelete && (
                            <IconActionBtn
                              type="button"
                              $variant="delete"
                              onClick={() => setConfirm({ type: "delete", cat })}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </IconActionBtn>
                          )}
                        </>
                      )}
                    </ActionRow>
                  </CategoryItem>
                ))
              )}
            </CategoryList>
          </ModalBody>
        </ModalShell>
      </ModalOverlay>

      {confirm && confirm.type === "delete" && (
        <DeletePopup
          text={`Are you sure you want to delete "${confirm.cat.name}"?`}
          buttonText="Delete"
          cancel={() => setConfirm(null)}
          Delete={handleConfirmAction}
        />
      )}

      {confirm && confirm.type === "toggle" &&
        createPortal(
          <ModalOverlay onClick={() => setConfirm(null)}>
            <ModalShell style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
              <ModalHero>
                <HeroRow>
                  <HeroText>
                    <HeroBadge style={{ color: "#f09a3e", borderColor: "rgba(240,154,62,0.2)" }}>
                      <AlertTriangle size={11} />
                      Confirm
                    </HeroBadge>
                    <ModalTitle>
                      {confirm.cat.is_active ? "Deactivate Category" : "Activate Category"}
                    </ModalTitle>
                    <ModalSubtitle>
                      {confirm.cat.is_active
                        ? `Deactivate "${confirm.cat.name}"?`
                        : `Activate "${confirm.cat.name}"?`}
                    </ModalSubtitle>
                  </HeroText>
                  <CloseBtn type="button" onClick={() => setConfirm(null)} aria-label="Close">
                    <X size={18} />
                  </CloseBtn>
                </HeroRow>
              </ModalHero>
              <ModalBody>
                <ConfirmText>
                  {confirm.cat.is_active
                    ? "Inactive categories won't appear when registering new equipment."
                    : "This category will be available for new equipment again."}
                </ConfirmText>
              </ModalBody>
              <ConfirmFooter>
                <SecondaryBtn type="button" onClick={() => setConfirm(null)}>
                  Cancel
                </SecondaryBtn>
                <PrimaryBtn type="button" onClick={handleConfirmAction}>
                  {confirm.cat.is_active ? "Deactivate" : "Activate"}
                </PrimaryBtn>
              </ConfirmFooter>
            </ModalShell>
          </ModalOverlay>,
          document.body
        )}
    </>,
    document.body
  );
};

export default CategoryManagerModal;
